'use client';

import * as React from 'react';
import { useAuth, useSignIn, useSignUp } from '@clerk/nextjs';
import { OAuthStrategy } from '@clerk/types';
import Link from 'next/link';

export default function ScrollPaywall() {
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded: signInLoaded, signIn, setActive: setSignInActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setSignUpActive } = useSignUp();

  const [showPaywall, setShowPaywall] = React.useState(false);
  const [hasTriggered, setHasTriggered] = React.useState(false);
  const [view, setView] = React.useState<'splash' | 'signin' | 'signup' | 'verify'>('splash');

  // Form State   
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [code, setCode] = React.useState('');

  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // 🛡️ AUTO-DISMISSAL LOGIC
  React.useEffect(() => {
    if (authLoaded && isSignedIn && showPaywall) {
      closePaywall();
    }
  }, [isSignedIn, authLoaded, showPaywall]);

  React.useEffect(() => {
    if (!authLoaded || isSignedIn || hasTriggered) return;

    const handleScroll = () => {
      if (!authLoaded || isSignedIn) return;

      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const scrollPercentage = (scrolled / scrollHeight) * 100;

      if (scrollPercentage >= 50 && !hasTriggered) {
        setShowPaywall(true);
        setHasTriggered(true);
        document.body.style.overflow = 'hidden';
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSignedIn, authLoaded, hasTriggered]);

  const closePaywall = () => {
    setShowPaywall(false);
    document.body.style.overflow = 'unset';
  };

  // 📝 AUTH LOGIC
  const handleSocialSignIn = (strategy: OAuthStrategy) => {
    if (!signInLoaded) return;
    signIn.authenticateWithRedirect({
      strategy,
      redirectUrl: '/sign-in/sso-callback',
      redirectUrlComplete: window.location.href, // STAY IN PLACE
    });
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInLoaded) return;
    setIsSubmitting(true);
    setError('');

    try {
      const result = await signIn.create({ identifier: email, password });
      if (result.status === 'complete') {
        await setSignInActive({ session: result.createdSessionId });
      } else if (result.status === 'needs_second_factor') {
        setView('verify');
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Sign in failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpLoaded) return;
    setIsSubmitting(true);
    setError('');

    try {
      await signUp.create({ firstName, lastName, emailAddress: email, password });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setView('verify');
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Sign up failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (view === 'verify' && signUpLoaded && signUp.status === 'missing_requirements') {
        const result = await signUp.attemptEmailAddressVerification({ code });
        if (result.status === 'complete') await setSignUpActive({ session: result.createdSessionId });
      } else if (signInLoaded) {
        const result = await signIn.attemptSecondFactor({ strategy: 'email_code', code });
        if (result.status === 'complete') await setSignInActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Verification failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showPaywall || isSignedIn) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in duration-1000">
      <div className="w-full max-w-md bg-white rounded-sm shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-700">

        <div id="clerk-captcha" /> {/* 🛡️ BOT PROTECTION */}

        {/* ═══ PERSISTENT BRANDED HEADER — Visible on ALL views ═══ */}
        <div className="w-full bg-brand flex flex-col items-center justify-center py-5 px-6 border-b border-brand/20">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-5 bg-white/70 rounded-full" />
            <h2 className="brand-logo-font text-2xl text-white tracking-tight leading-none">World Prime Post</h2>
            <div className="w-1.5 h-5 bg-white/70 rounded-full" />
          </div>
          <p className="brand-tagline-font text-[8px] uppercase tracking-[0.5em] text-white/90 mt-1.5 font-black">
            Decoding the Global Shift · Member Access
          </p>
        </div>

        {view === 'splash' && (
          <div className="flex flex-col items-center">
            <div className="p-8 flex flex-col items-center text-center">
              <h3 className="brand-logo-font text-3xl text-[#111111] leading-tight mb-6 mt-2">Authentic Intelligence Requires a Key.</h3>
              <p className="text-[13px] text-muted-foreground/80 italic mb-8 px-4">You&apos;ve reached the structural limit of our public feed. Create your complimentary dossier to continue decoding the global shift.</p>
              <button onClick={() => setView('signin')} className="w-full h-14 bg-brand text-white text-[11px] font-black uppercase tracking-[0.2em]">Register or Log In to Continue</button>
            </div>
          </div>
        )}

        {view === 'signin' && (
          <div className="p-8 flex flex-col items-center animate-in zoom-in-95 duration-500">
            <h2 className="brand-logo-font text-2xl text-[#111111] mb-6">Log in to World Prime Post</h2>
            <form onSubmit={handleSignIn} className="w-full space-y-3.5">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="Email address" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="Password" />
              {error && <p className="text-[9px] text-brand font-bold uppercase text-center">{error}</p>}
              <button type="submit" disabled={isSubmitting} className="w-full h-11 bg-[#111111] text-white text-[10px] font-black uppercase tracking-[0.2em]">{isSubmitting ? 'Verifying...' : 'Continue'}</button>
            </form>
            <div className="mt-6 text-center">
              <button onClick={() => setView('signup')} className="text-[10px] font-black uppercase tracking-widest text-brand underline decoration-brand/30">Create an account instead</button>
            </div>
          </div>
        )}

        {view === 'signup' && (
          <div className="p-8 flex flex-col items-center animate-in zoom-in-95 duration-500">
            <h2 className="brand-logo-font text-2xl text-[#111111] mb-6">Create your account</h2>
            <form onSubmit={handleSignUp} className="w-full space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="First Name" />
                <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="Last Name" />
              </div>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="Email address" />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 h-11 border border-gray-200 focus:border-[#111111] outline-none text-[14px]" placeholder="Set Password" />
              {error && <p className="text-[9px] text-brand font-bold uppercase text-center">{error}</p>}
              <button type="submit" disabled={isSubmitting} className="w-full h-11 bg-[#111111] text-white text-[10px] font-black uppercase tracking-[0.2em]">{isSubmitting ? 'Processing...' : 'Register'}</button>
            </form>
            <div className="mt-6 text-center">
              <button onClick={() => setView('signin')} className="text-[10px] font-black uppercase tracking-widest text-brand underline decoration-brand/30">Already have an account? Log in</button>
            </div>
          </div>
        )}

        {view === 'verify' && (
          <div className="p-8 flex flex-col items-center animate-in zoom-in-95 duration-500">
            <h2 className="brand-logo-font text-2xl text-[#111111] mb-6">Verify Secure Access</h2>
            <p className="text-[11px] text-center text-muted-foreground/70 mb-6">Enter the verification code sent to your email.</p>
            <form onSubmit={handleVerify} className="w-full space-y-3.5">
              <input type="text" required value={code} onChange={(e) => setCode(e.target.value)} className="w-full px-4 h-11 border border-gray-200 text-center tracking-[0.5em] font-black text-[15px]" placeholder="000000" />
              {error && <p className="text-[9px] text-brand font-bold uppercase text-center">{error}</p>}
              <button type="submit" disabled={isSubmitting} className="w-full h-11 bg-[#111111] text-white text-[10px] font-black uppercase tracking-[0.2em]">{isSubmitting ? 'Verifying...' : 'Unlock Now'}</button>
            </form>
          </div>
        )}

        {(view === 'signin' || view === 'signup') && (
          <div className="px-8 pb-8 flex flex-col gap-3">
            <div className="w-full flex items-center gap-3 my-2">
              <div className="h-[1px] flex-grow bg-gray-100" />
              <span className="text-[8px] uppercase font-bold text-gray-400 tracking-[0.2em]">or</span>
              <div className="h-[1px] flex-grow bg-gray-100" />
            </div>
            <button onClick={() => handleSocialSignIn('oauth_google')} className="w-full h-11 bg-white border border-gray-200 flex items-center justify-center gap-3 hover:border-[#111111] transition-all">
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
                <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.712s.102-1.173.282-1.712V4.956H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.044l3.007-2.332z" fill="#FBBC05" />
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.956L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
              </svg>
              <span className="text-[9px] font-black uppercase tracking-widest text-[#111111]">Continue with Google</span>
            </button>
          </div>
        )}

        {/* ═══ BRANDED FOOTER ═══ */}
        <div className="w-full border-t border-gray-200 py-3 px-6 flex items-center justify-center gap-2 bg-gray-50">
          <span className="text-[8px] font-black uppercase tracking-[0.3em] text-gray-500">©</span>
          <a href="https://worldprimepost.com" className="text-[8px] font-black uppercase tracking-[0.3em] text-gray-600 hover:text-brand transition-colors">worldprimepost.com</a>
          <span className="text-[8px] font-black uppercase tracking-[0.3em] text-gray-500">· Secure Member Portal</span>
        </div>

      </div>
    </div>
  );
}
